import { Color, Vec2 } from 'kaplay';
import { Colors } from '../constants/colors';
import { Tags } from '../constants/tags';
import { k } from '../main';
import { getMessageForTone } from '../nls/statements';
import { GameState } from '../types/game-state';

export function addChildCircle({
  color,
  pos,
  sceneWidth,
  sceneHeight,
  gameState,
}: {
  pos: Vec2;
  color: Color;
  gameState: GameState;
  sceneWidth?: number;
  sceneHeight?: number;
}) {
  const MOVE_SPEED = 7; // Slightly faster than player to stay ahead
  const PLAYER_AVOIDANCE_DISTANCE = 300; // Distance to start fleeing from player
  const NPC_AVOIDANCE_DISTANCE = 100; // Distance to start avoiding NPCs
  const AVOIDANCE_FORCE = 2; // How strong the avoidance is
  const WALL_BOUNCE_FORCE = 1.5; // How much to bounce off walls
  const BORDER_AVOIDANCE_DISTANCE = 100; // Distance from border to start turning away

  const childCircle = add([
    k.pos(pos),
    circle(20), // Smaller than player (32)
    area(),
    body(),
    Tags.KID,
  ]);

  let velocity = vec2(0, 0);
  const previousPositions: Vec2[] = []; // Track recent positions for stuck detection
  let stuckCounter = 0;
  const MAX_POSITION_HISTORY = 30; // Frames to track
  const STUCK_THRESHOLD = 5; // Distance threshold to consider stuck
  const EMERGENCY_FORCE = 8; // Force to apply when stuck

  childCircle.onDraw(() => {
    drawCircle({
      color: rgb(...(Colors.BLACK as [number, number, number])),
      radius: 20,
    });
    drawCircle({
      color,
      radius: 18,
    });
  });

  childCircle.onUpdate(() => {
    // Get all relevant entities
    const player = get(Tags.PLAYER)[0];
    const npcs = get(Tags.NPC);
    // We'll attach game state language to player circle if available elsewhere via (player as any).language
    // Fallback to english if not provided.
    // Track last time the child spoke to rate limit messages.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(childCircle as any)._childSpeechState) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (childCircle as any)._childSpeechState = { lastSpokeAt: 0 };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const speechState = (childCircle as any)._childSpeechState as { lastSpokeAt: number };

    if (!player) return;

    // Track position history for stuck detection
    previousPositions.push(childCircle.pos.clone());
    if (previousPositions.length > MAX_POSITION_HISTORY) {
      previousPositions.shift();
    }

    // Check if stuck (hasn't moved much in recent frames)
    let isStuck = false;
    if (previousPositions.length >= MAX_POSITION_HISTORY) {
      const oldPos = previousPositions[0];
      const currentPos = childCircle.pos;
      const totalMovement = oldPos.dist(currentPos);

      if (totalMovement < STUCK_THRESHOLD) {
        stuckCounter++;
        if (stuckCounter > 10) {
          // Stuck for multiple frames
          isStuck = true;
        }
      } else {
        stuckCounter = 0;
      }
    }

    let avoidanceForce = vec2(0, 0);

    // Border avoidance - check proximity to scene boundaries and turn away proactively
    if (sceneWidth && sceneHeight) {
      const currentPos = childCircle.pos;

      // Check distance to each border and apply avoidance force
      const leftDist = currentPos.x;
      const rightDist = sceneWidth - currentPos.x;
      const topDist = currentPos.y;
      const bottomDist = sceneHeight - currentPos.y;

      // Left border avoidance
      if (leftDist < BORDER_AVOIDANCE_DISTANCE) {
        const force = (BORDER_AVOIDANCE_DISTANCE - leftDist) / BORDER_AVOIDANCE_DISTANCE;
        avoidanceForce = avoidanceForce.add(vec2(force * AVOIDANCE_FORCE * 2, 0));
      }

      // Right border avoidance
      if (rightDist < BORDER_AVOIDANCE_DISTANCE) {
        const force = (BORDER_AVOIDANCE_DISTANCE - rightDist) / BORDER_AVOIDANCE_DISTANCE;
        avoidanceForce = avoidanceForce.add(vec2(-force * AVOIDANCE_FORCE * 2, 0));
      }

      // Top border avoidance
      if (topDist < BORDER_AVOIDANCE_DISTANCE) {
        const force = (BORDER_AVOIDANCE_DISTANCE - topDist) / BORDER_AVOIDANCE_DISTANCE;
        avoidanceForce = avoidanceForce.add(vec2(0, force * AVOIDANCE_FORCE * 2));
      }

      // Bottom border avoidance
      if (bottomDist < BORDER_AVOIDANCE_DISTANCE) {
        const force = (BORDER_AVOIDANCE_DISTANCE - bottomDist) / BORDER_AVOIDANCE_DISTANCE;
        avoidanceForce = avoidanceForce.add(vec2(0, -force * AVOIDANCE_FORCE * 2));
      }
    }

    // Emergency movement when stuck
    if (isStuck) {
      // Apply a strong random force to escape
      const randomAngle = Math.random() * Math.PI * 2;
      const emergencyDirection = vec2(Math.cos(randomAngle), Math.sin(randomAngle));
      avoidanceForce = avoidanceForce.add(emergencyDirection.scale(EMERGENCY_FORCE));
      stuckCounter = 0; // Reset counter after applying emergency force
    }

    // Wall avoidance - look ahead to avoid walls before colliding
    const walls = get(Tags.WALL);
    const WALL_AVOIDANCE_DISTANCE = 60;
    walls.forEach((wall) => {
      const wallDistance = childCircle.pos.dist(wall.pos);
      if (wallDistance < WALL_AVOIDANCE_DISTANCE && wallDistance > 0) {
        const wallDirection = childCircle.pos.sub(wall.pos).unit();
        const wallForceStrength =
          (WALL_AVOIDANCE_DISTANCE - wallDistance) / WALL_AVOIDANCE_DISTANCE;
        avoidanceForce = avoidanceForce.add(
          wallDirection.scale(wallForceStrength * AVOIDANCE_FORCE * 1.5),
        );
      }
    });

    // Calculate avoidance from player - strongest force, but consider borders
    const playerDistance = childCircle.pos.dist(player.pos);
    if (playerDistance < PLAYER_AVOIDANCE_DISTANCE && playerDistance > 0) {
      let playerDirection = childCircle.pos.sub(player.pos).unit();
      const playerForceStrength =
        (PLAYER_AVOIDANCE_DISTANCE - playerDistance) / PLAYER_AVOIDANCE_DISTANCE;

      // If avoiding player would push child towards a border, adjust direction
      if (sceneWidth && sceneHeight) {
        const futurePos = childCircle.pos.add(playerDirection.scale(50)); // Look ahead
        const safetyMargin = 50;

        // Check if the avoidance direction would push towards borders
        if (
          futurePos.x < safetyMargin ||
          futurePos.x > sceneWidth - safetyMargin ||
          futurePos.y < safetyMargin ||
          futurePos.y > sceneHeight - safetyMargin
        ) {
          // Find a safer direction - try perpendicular directions
          const perpendicular1 = vec2(-playerDirection.y, playerDirection.x);
          const perpendicular2 = vec2(playerDirection.y, -playerDirection.x);

          const future1 = childCircle.pos.add(perpendicular1.scale(50));
          const future2 = childCircle.pos.add(perpendicular2.scale(50));

          // Choose the perpendicular direction that stays further from borders
          const dist1FromBorder = Math.min(
            future1.x,
            sceneWidth - future1.x,
            future1.y,
            sceneHeight - future1.y,
          );
          const dist2FromBorder = Math.min(
            future2.x,
            sceneWidth - future2.x,
            future2.y,
            sceneHeight - future2.y,
          );

          if (dist1FromBorder > safetyMargin || dist2FromBorder > safetyMargin) {
            playerDirection = dist1FromBorder > dist2FromBorder ? perpendicular1 : perpendicular2;
          }
        }
      }

      avoidanceForce = avoidanceForce.add(
        playerDirection.scale(playerForceStrength * AVOIDANCE_FORCE * 2),
      );

      // Attempt to speak (child tone) no more frequently than every 5 seconds.
      const now = time();
      const COOLDOWN = 5;
      if (now - speechState.lastSpokeAt >= COOLDOWN) {
        // 40% chance to speak when conditions met
        if (Math.random() < 0.4) {
          speechState.lastSpokeAt = now;
          const message = getMessageForTone('child', gameState.playerKnownLanguage);
          const textObj = add([
            k.text(message, { size: 14 }),
            k.pos(childCircle.pos.x, childCircle.pos.y - 40),
            k.color(0, 0, 0),
            anchor('center'),
          ]);
          wait(2, () => destroy(textObj));
        }
      }
    }

    // Calculate avoidance from NPCs - weaker force, just to go around them
    npcs.forEach((npc) => {
      const npcDistance = childCircle.pos.dist(npc.pos);
      if (npcDistance < NPC_AVOIDANCE_DISTANCE && npcDistance > 0) {
        const npcDirection = childCircle.pos.sub(npc.pos).unit();
        const npcForceStrength = (NPC_AVOIDANCE_DISTANCE - npcDistance) / NPC_AVOIDANCE_DISTANCE;
        avoidanceForce = avoidanceForce.add(
          npcDirection.scale(npcForceStrength * AVOIDANCE_FORCE * 0.7),
        );
      }
    });

    // Apply forces to velocity
    velocity = velocity.add(avoidanceForce);

    // Apply some damping to prevent infinite acceleration
    velocity = velocity.scale(0.95);

    // Limit max speed
    const maxSpeed = MOVE_SPEED;
    if (velocity.len() > maxSpeed) {
      velocity = velocity.unit().scale(maxSpeed);
    }

    // Move the child circle
    if (velocity.len() > 0.1) {
      // Only move if there's significant velocity
      childCircle.moveBy(velocity.x, velocity.y);
    }
  });

  // Handle wall collisions - improved bounce with corner detection
  childCircle.onCollide(Tags.WALL, (wall) => {
    // Calculate bounce direction
    const wallCenter = wall.pos;
    const childCenter = childCircle.pos;
    let bounceDirection = childCenter.sub(wallCenter).unit();

    // Check for nearby walls to detect corners
    const walls = get(Tags.WALL);
    const CORNER_DETECTION_DISTANCE = 80;
    let nearbyWalls = 0;
    let combinedWallDirection = vec2(0, 0);

    walls.forEach((nearWall) => {
      const distToWall = childCircle.pos.dist(nearWall.pos);
      if (distToWall < CORNER_DETECTION_DISTANCE) {
        nearbyWalls++;
        const wallDir = childCenter.sub(nearWall.pos).unit();
        combinedWallDirection = combinedWallDirection.add(wallDir);
      }
    });

    // If in a corner (multiple nearby walls), use combined direction
    if (nearbyWalls > 1) {
      bounceDirection = combinedWallDirection.unit();
      // Apply stronger force when in corner
      velocity = velocity.add(bounceDirection.scale(WALL_BOUNCE_FORCE * 2));
    } else {
      // Normal wall bounce
      velocity = velocity.add(bounceDirection.scale(WALL_BOUNCE_FORCE));
    }

    // Move away from wall more aggressively to prevent getting stuck
    const pushDistance = nearbyWalls > 1 ? 5 : 3;
    childCircle.moveBy(bounceDirection.x * pushDistance, bounceDirection.y * pushDistance);
  });

  return childCircle;
}
